/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Popper, InputBase, ClickAwayListener } from '@material-ui/core';
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Group as GroupIcon,
  AccountCircle as AccountCircleIcon,
  ArrowDropUp as ArrowDropUpIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
import api from '~/services';
import * as actions from '~/store/actions';
import { ToastMessage, useTranslation } from '~/dependencies';
import useTypedSelector from '~/hooks/useTypedSelector';
import { Conversation } from '~/interfaces/conversations';
import { Department, User } from '~/types/accountInfo';

import { useStyles, AssignedChip, GroupHeader, GroupItems } from './styles';

type AssignedAgentSelectorProps = {
  conversationId: string;
  agentName: string | undefined;
};

type Unassigned = {
  name: 'Unassigned';
};

type Option = Unassigned | User | Department;

export default function AssignedAgentSelector({
  conversationId,
  agentName,
}: AssignedAgentSelectorProps) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const isAdmin = useTypedSelector(state => state.auth.isAdmin);
  const usersList = useTypedSelector(state => state.accountInfo.users);
  const departmentsList = useTypedSelector(
    state => state.accountInfo.departments
  );

  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const defaultAgent: Unassigned = {
    name: 'Unassigned',
  };
  const isAssigned = !!agentName;
  const assignedName = isAssigned ? agentName : defaultAgent.name;

  const agentList = [defaultAgent, ...usersList, ...departmentsList];

  const isPopperOpen = Boolean(anchorEl);
  const componentId = isPopperOpen ? 'assigned-agent-selector' : undefined;

  /* Translate "Unassigned" in the Autocomplete options */
  const translateUnassignedI18n = (data: string) => {
    // if valid contact or group name, return as is
    if (data !== 'Unassigned') return data;
    // Cover english speaker's which can be using another softphone language
    if (i18n.language === 'en') return data;
    return t('screens.conversation.assignedAgentSelector.unassigned');
  };

  const handleChangeAgent = async (event: any, agentNewInfo: Option) => {
    try {
      event.stopPropagation();
      setAnchorEl(null);

      let newState: Pick<
        Conversation,
        'assignedDepartmentId' | 'assignedId' | 'assignedName'
      > = {
        assignedDepartmentId: null,
        assignedId: null,
        assignedName: '',
      };

      let payload: {
        department_id: number | null;
        user_id: number | null;
      } = {
        department_id: null,
        user_id: null,
      };

      // Unassigned as does NOT have id
      if ('id' in agentNewInfo) {
        if ('isAdmin' in agentNewInfo) {
          // isAdmin is only in user
          payload = {
            ...payload,
            user_id: agentNewInfo.id,
          };

          newState = {
            ...newState,
            assignedId: agentNewInfo.id,
            assignedName: agentNewInfo.name,
          };
        } else {
          // It is department or group
          payload = {
            ...payload,
            department_id: agentNewInfo.id,
          };

          newState = {
            ...newState,
            assignedDepartmentId: agentNewInfo.id,
            assignedName: agentNewInfo.name,
          };
        }
      }

      const response = await api.conversation.patch.assign(
        conversationId,
        payload
      );

      if (response.status === 200) {
        dispatch(
          actions.updateConversationAssignedAgent(conversationId, newState)
        );

        ToastMessage(
          'success',
          t('screens.conversation.assignedAgentSelector.reassignedSuccess', {
            assignedName: translateUnassignedI18n(agentNewInfo?.name),
          })
        );
      }
    } catch (error) {
      ToastMessage(
        'error',
        `An error occurred while re-assigning the conversation. Please try again.`
      );
    }
  };

  // Close popper when clicking outside
  const handleClickAway = () => {
    setAnchorEl(null);
  };

  const handleAssignedChipClick = (event: any) => {
    event.stopPropagation();
    // Only admin can change assigned agent
    if (!isAdmin) return;

    if (isPopperOpen) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget.parentElement);
    }
  };

  const renderGroup = (params: any) => {
    const getGroupIcon = (groupName: string) => {
      switch (groupName) {
        case 'users':
          return <AccountCircleIcon />;
        case 'departments':
          return <GroupIcon />;
        case 'unassigned':
          return <LinkOffIcon />;
        default:
          return <AccountCircleIcon />;
      }
    };

    return (
      <li key={params.key}>
        <GroupHeader
          onClick={(event: React.MouseEvent) => event.stopPropagation()}>
          {getGroupIcon(params.group)}
          {params.group === 'users' &&
            t('screens.conversation.assignedAgentSelector.agents')}
          {params.group === 'departments' &&
            t('screens.conversation.assignedAgentSelector.userGroups')}
          {params.group === 'unassigned' &&
            t('screens.conversation.assignedAgentSelector.unassigned')}
        </GroupHeader>
        <GroupItems>{params.children}</GroupItems>
      </li>
    );
  };

  const groupBy = (option: Option) =>
    'id' in option
      ? 'isAdmin' in option
        ? 'users'
        : 'departments'
      : 'unassigned';

  const renderOption = (option: Option) => (
    <div>{translateUnassignedI18n(option.name)}</div>
  );

  const getOptionLabel = (option: Option) => option.name;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div>
        <AssignedChip
          data-test-id="assign-selector"
          aria-describedby={componentId}
          onClick={handleAssignedChipClick}
          label={translateUnassignedI18n(String(assignedName))}
          size="small"
          variant={isAssigned ? 'outlined' : 'contained'}
          icon={isAssigned ? <LinkIcon /> : <LinkOffIcon />}
          clickable
          {...(isAdmin && {
            deleteIcon: anchorEl ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />,
            onDelete: handleAssignedChipClick,
          })}
        />

        <Popper
          id={componentId}
          open={isPopperOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          className={classes.popper}>
          <Autocomplete
            open
            disablePortal
            forcePopupIcon
            disableCloseOnSelect
            options={agentList || []}
            groupBy={groupBy}
            renderGroup={renderGroup}
            renderOption={renderOption}
            getOptionLabel={getOptionLabel}
            // @ts-ignore
            onChange={handleChangeAgent}
            noOptionsText={t(
              'screens.conversation.assignedAgentSelector.noResultsFound'
            )}
            renderInput={params => (
              <InputBase
                autoFocus
                ref={params.InputProps.ref}
                inputProps={params.inputProps}
                placeholder={t(
                  'screens.conversation.assignedAgentSelector.searchAgentOrGroups'
                )}
                className={classes.inputBase}
                onClick={event => event.stopPropagation()}
              />
            )}
            classes={{
              paper: classes.paper,
              option: classes.option,
              popperDisablePortal: classes.popperDisablePortal,
            }}
          />
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
